import { ArrowUpRight, Phone, MessageCircle } from "lucide-react";
import { whatsappLink, telLink, CLINIC } from "@/lib/clinic";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-24 md:pt-32 pb-16 md:pb-28 overflow-hidden"
    >
      {/* Decorative ripple in background */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[640px] h-[640px] ripple-deco opacity-70" />
      <div className="pointer-events-none absolute top-1/2 -right-60 w-[520px] h-[520px] ripple-deco opacity-60" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-12 gap-6 md:gap-8 items-end">
          {/* Left text — 8 cols */}
          <div className="col-span-12 lg:col-span-8 animate-fade-up">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-forest-700"
            >
              <span className="w-10 h-px bg-terracotta-500" />
              Physiotherapy · Mylapore, Chennai
            </span>

            <h1
              data-testid="hero-heading"
              className="mt-6 font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tighter text-forest-900"
            >
              Move better.
              <br />
              <span className="italic text-terracotta-500">Live</span> better.
            </h1>

            <p className="mt-8 max-w-xl text-lg md:text-xl leading-relaxed text-forest-700/85">
              Expert physiotherapy for pain relief, injury recovery and lasting
              mobility. Personalised plans across orthopedic, sports and
              post-surgical rehabilitation — your ripple of change starts here.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-sand-50 pl-7 pr-5 py-4 text-sm tracking-wide transition-all hover:-translate-y-1 shadow-[0_10px_30px_-12px_rgba(196,91,65,0.55)]"
              >
                <MessageCircle size={18} strokeWidth={1.75} />
                Chat on WhatsApp
                <span className="ml-1 inline-flex items-center justify-center w-8 h-8 rounded-full bg-sand-50 text-terracotta-500 transition-transform group-hover:rotate-45">
                  <ArrowUpRight size={16} />
                </span>
              </a>

              <a
                href={telLink()}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-3 rounded-full border border-forest-900/30 hover:border-forest-900 text-forest-900 px-7 py-4 text-sm tracking-wide transition-all hover:bg-forest-900 hover:text-sand-50"
              >
                <Phone size={16} strokeWidth={1.75} />
                {CLINIC.phoneDisplay}
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 max-w-md gap-6 border-t border-forest-900/15 pt-6">
              <Stat k="200+" v="Patients" />
              <Stat k="8+" v="Yrs Practice" />
              <Stat k="4.9★" v="Avg Rating" />
            </div>
          </div>

          {/* Right image — 4 cols */}
          <div className="col-span-12 lg:col-span-4 lg:pb-2">
            <figure className="relative overflow-hidden rounded-[2rem] aspect-[3/4] lg:aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/5794059/pexels-photo-5794059.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700"
                alt="Therapist guiding a patient through recovery exercises"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-105"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-900/55 via-transparent to-transparent" />
              <figcaption className="absolute bottom-5 left-5 right-5 text-sand-50">
                <p className="text-xs tracking-[0.28em] uppercase opacity-80">
                  In-person & Home Visits
                </p>
                <p className="font-serif text-2xl mt-1 leading-tight">
                  Hands-on care, root-cause healing.
                </p>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ k, v }) {
  return (
    <div data-testid={`hero-stat-${v.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-serif text-3xl md:text-4xl text-forest-900">{k}</div>
      <div className="text-xs tracking-[0.2em] uppercase text-forest-700/70 mt-1">
        {v}
      </div>
    </div>
  );
}
